import axiosInstanceWithoutInterceptor from "../axiosWithoutInterceptor";
import getConfig from "../config";
import { LocaleConfig } from "./i18n.config";

const LOCALES_API_PATH = "/api/v1/locales";

/** Shape of the /api/v1/locales response */
interface LocalesApiResponse {
    portal?: {
        released?: string[];
        targeted?: string[];
    };
    elr?: {
        released?: string[];
        targeted?: string[];
    };
}

/**
 * Derives a human-readable display label for a BCP 47 locale code.
 * Uses the browser's `Intl.DisplayNames` API to produce a native label
 * in the locale's own language (e.g. "fr-CA" → "français (Canada)"),
 * then title-cases the first character.
 *
 * Falls back to the raw code if `Intl.DisplayNames` is unavailable.
 */
export function getLocaleLabel(code: string): string {
    try {
        const displayNames = new Intl.DisplayNames([code], { type: "language" });
        const label = displayNames.of(code);
        if (label) {
            return label.charAt(0).toUpperCase() + label.slice(1);
        }
    } catch {
        // Intl.DisplayNames not supported in this environment — fall back to code
    }
    return code;
}

/**
 * Fetches the list of released portal locales from the Avalara data platform.
 *
 * Endpoint: `{dataService}/api/v1/locales`
 * Uses the `portal.released` array from the response, which represents
 * locales that have been fully released for the Avalara portal suite.
 *
 * @returns Array of `LocaleConfig` objects ready for use in the locale switcher,
 *          or an empty array if the request fails.
 */
export async function fetchSupportedLocales(): Promise<LocaleConfig[]> {
    const { dataService } = getConfig().hostnames;

    try {
        const { data } = await axiosInstanceWithoutInterceptor.get<LocalesApiResponse>(
            `${dataService}${LOCALES_API_PATH}`,
            { baseURL: undefined, timeout: 5000 }
        );

        const releasedCodes = data?.portal?.released ?? [];

        return releasedCodes.map(code => ({
            code,
            label: getLocaleLabel(code),
        }));
    } catch {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch supported locales from data platform");
        return [];
    }
}
